import { AxiosError } from "axios";

import { apiMeta } from "@/lib/axios";

/**
 * Serviço para integração com a Meta API para WhatsApp Business
 * Implementa as chamadas necessárias para subscribed_apps e register
 */
export class MetaIntegrationService {
  /**
   * Inscreve o app na WhatsApp Business Account (WABA)
   * POST https://graph.facebook.com/v19.0/{waba_id}/subscribed_apps
   */
  async subscribeApp(wabaId: string): Promise<void> {
    try {
      await apiMeta.post(`${wabaId}/subscribed_apps`);
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(
          `Failed to subscribe app to WABA ${wabaId}: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error(`Failed to subscribe app to WABA ${wabaId}`);
    }
  }

  /**
   * Registra o número de telefone na Meta
   * POST https://graph.facebook.com/v19.0/{phone_number_id}/register
   */
  async registerPhoneNumber(phoneNumberId: string, pin: string): Promise<void> {
    try {
      await apiMeta.post(`${phoneNumberId}/register`, {
        messaging_product: "whatsapp",
        pin: pin,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(
          `Failed to register phone number ${phoneNumberId}: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error(`Failed to register phone number ${phoneNumberId}`);
    }
  }

  /**
   * Desregistra o número de telefone na Meta
   * POST https://graph.facebook.com/v19.0/{phone_number_id}/deregister
   */
  async deregisterPhoneNumber(phoneNumberId: string): Promise<void> {
    try {
      await apiMeta.post(`${phoneNumberId}/deregister`, {});
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(
          `Failed to deregister phone number ${phoneNumberId}: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error(`Failed to deregister phone number ${phoneNumberId}`);
    }
  }

  /**
   * Remove a inscrição do app na WhatsApp Business Account
   * DELETE https://graph.facebook.com/v19.0/{waba_id}/subscribed_apps
   */
  async unsubscribeApp(wabaId: string): Promise<void> {
    try {
      await apiMeta.delete(`${wabaId}/subscribed_apps`);
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(
          `Failed to unsubscribe app from WABA ${wabaId}: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error(`Failed to unsubscribe app from WABA ${wabaId}`);
    }
  }

  /**
   * Obtém informações do número de telefone
   * GET https://graph.facebook.com/v19.0/{waba_id}/phone_numbers
   */
  async getPhoneNumbers(wabaId: string): Promise<{
    display_phone_number: string;
    verified_name: string;
  }> {
    try {
      const response = await apiMeta.get(`${wabaId}/phone_numbers`);
      const phoneData = response?.data?.data?.[0];

      if (!phoneData) {
        throw new Error("No phone number data found");
      }

      return {
        display_phone_number: phoneData.display_phone_number,
        verified_name: phoneData.verified_name,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(
          `Failed to get phone numbers for WABA ${wabaId}: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error(`Failed to get phone numbers for WABA ${wabaId}`);
    }
  }

  /**
   * Obtém a URL da foto de perfil do WhatsApp Business
   * GET https://graph.facebook.com/v19.0/{phone_number_id}/whatsapp_business_profile?fields=profile_picture_url
   */
  async getProfilePictureUrl(phoneNumberId: string): Promise<string | null> {
    try {
      const response = await apiMeta.get(
        `${phoneNumberId}/whatsapp_business_profile?fields=profile_picture_url`
      );

      // Usar o mesmo padrão do projeto legado
      const { profile_picture_url } = response?.data?.data?.[0] ?? {};

      return profile_picture_url || null;
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(
          `Failed to get profile picture for phone number ${phoneNumberId}: ${error.response?.data?.message || error.message}`
        );
      }
      throw new Error(`Failed to get profile picture for phone number ${phoneNumberId}`);
    }
  }
}

export const metaIntegrationService = new MetaIntegrationService();
